import { useLocation, useNavigate } from "react-router-dom";
import useLanguage from "./useLanguage.js";
import usePageLanguage from "./usePageLanguage.js";
import { hasLocalizedPage, localizedPath } from "../content/pages/registry.js";
import { stripLanguagePrefix } from "../lib/language.js";

/**
 * The language control, as a question about URLs rather than about state.
 *
 * The switcher used to call `i18n.changeLanguage` and nothing else: the copy
 * changed, the URL did not, and Greek had no address a visitor could bookmark,
 * share or a crawler could index. Language now belongs to the URL, so switching
 * language is a navigation — `/wedding-planning` ↔ `/el/wedding-planning` —
 * resolved through the page registry so the control can never offer a Greek URL
 * that does not exist.
 *
 * ── When the page has no counterpart ──────────────────────────────────────
 * The switcher deliberately does NOT use `localizedPath`'s fall back to
 * English. Being handed the English URL is right for a link inside a sentence
 * and wrong for a control that says «Ελληνικά» — and sending the visitor to the
 * Greek homepage instead would be worse still, dropping them somewhere they did
 * not ask to be.
 *
 * So it falls back to switching the language in place, on the same page.
 * `available: false` is exposed alongside, so a caller that would rather
 * disable the control can, without this hook deciding that for it.
 *
 * ── What is left of that fallback ─────────────────────────────────────────
 * Ten of the site's pages now have Greek URLs, and on every one of them this
 * hook navigates. The in-place fallback survives for the pages that have none:
 * the guides index, whose Greek copy exists but has no document yet, the
 * fourteen guides, which have no Greek copy at all, and the `/home` alias.
 *
 * It is deliberately still here rather than replaced by a bounce to the Greek
 * homepage. On those pages there is genuinely nowhere to go, and dropping a
 * reader of an English guide onto the Greek homepage loses their place to no
 * purpose. It retires by itself when the guides are translated — at which point
 * `hasLocalizedPage` answers `true` everywhere and this branch stops being
 * reachable.
 *
 * Its two companions in the original stopgap are already gone: the
 * `data-prerender-lang` guard has been removed from every static document, and
 * `?lang=` is a redirect rather than a render mode wherever a Greek URL exists.
 */
export default function useLanguageSwitcher() {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const { setLanguage } = useLanguage();

  // The URL wins where it says anything; otherwise the rendered language is
  // the honest answer, which is what keeps the control's label correct while
  // unprefixed paths can still render Greek.
  const language = usePageLanguage();
  const basePath = stripLanguagePrefix(pathname);

  /** Whether `target` has a real page to switch to. */
  const isAvailable = (target) =>
    target === language || hasLocalizedPage(basePath, target);

  /** The URL a switch to `target` would go to, or `null` if there is none. */
  const hrefFor = (target) =>
    hasLocalizedPage(basePath, target)
      ? `${localizedPath(basePath, target)}${search}${hash}`
      : null;

  const switchTo = (target) => {
    if (target === language) return;

    // Set the language either way: on the Greek subtree `useRouteLanguage`
    // would do it anyway, but leaving the English tree is a plain navigation
    // with no such hook, and a stored Greek preference would otherwise render
    // the English URL in Greek.
    setLanguage(target);

    const href = hrefFor(target);
    if (href) navigate(href);
  };

  return { language, isAvailable, hrefFor, switchTo };
}
